import { useState } from "react";
import type { KeyboardEvent } from "react";

import { useTranslate } from "@similarweb/i18n-provider";
import { ChevronRight } from "@similarweb/icons";
import { ArrowUp } from "@similarweb/icons";
import { ClicksBlue } from "@similarweb/icons/illustrations";
import { colorsPalettes } from "@similarweb/styles";

import { swSettings } from "app/common/services/swSettings";

import { onboardingAgentTrackingService } from "../onboardingAgentTrackingService";
import type { OnboardingAgentViewportPosition } from "../OnboardingAgent/OnboardingAgent.types";
import {
    ONBOARDING_AGENT_INPUT_SEND_ARIA,
    ONBOARDING_AGENT_NUDGE_DISMISS_ARIA,
    ONBOARDING_AGENT_NUDGE_GREETING_FALLBACK,
    ONBOARDING_AGENT_NUDGE_GREETING_TITLE,
    ONBOARDING_AGENT_NUDGE_INPUT_PLACEHOLDER,
} from "../texts";

import { useOnboardingAgentNudgePlacement } from "./hooks/useOnboardingAgentNudgePlacement";
import {
    CloseButton,
    NudgeCard,
    NudgeGreetingTitle,
    NudgeGreetingTitleRow,
    NudgeHeader,
    NudgeInputRow,
    NudgeInputSendButton,
    NudgeSlot,
    NudgeSuggestionIcon,
    NudgeSuggestionItem,
    NudgeSuggestionList,
    NudgeSuggestionText,
    NudgeTextInput,
} from "./styles";

type OnboardingAgentNudgeProps = {
    position: OnboardingAgentViewportPosition;
    isVisible: boolean;
    suggestions: readonly string[];
    onAsk: (question: string) => void;
    onDismiss: () => void;
};

export const OnboardingAgentNudge = ({
    position,
    isVisible,
    suggestions,
    onAsk,
    onDismiss,
}: OnboardingAgentNudgeProps) => {
    const { translate } = useTranslate();
    const [inputValue, setInputValue] = useState("");
    const { slotRef, slotPosition, placement } = useOnboardingAgentNudgePlacement(position, isVisible);
    const firstName = String(swSettings?.user?.firstname ?? "").trim();
    const canSend = inputValue.trim().length > 0;

    if (!isVisible) {
        return null;
    }

    const handleInputSubmit = (): void => {
        const trimmed = inputValue.trim();
        if (!trimmed) {
            return;
        }
        setInputValue("");
        onAsk(trimmed);
    };

    const handleInputKeyDown = (event: KeyboardEvent<HTMLInputElement>): void => {
        if (event.key === "Enter") {
            event.preventDefault();
            handleInputSubmit();
        }
    };

    return (
        <NudgeSlot
            ref={slotRef}
            $x={slotPosition.x}
            $y={slotPosition.y}
            data-automation="onboarding-agent-nudge-root"
        >
            <NudgeCard $placement={placement}>
                <NudgeHeader>
                    <NudgeGreetingTitleRow>
                        <NudgeGreetingTitle
                            fontVariant="h5_2"
                            color={colorsPalettes.blue[400]}
                        >
                            {firstName
                                ? translate(ONBOARDING_AGENT_NUDGE_GREETING_TITLE, { name: firstName })
                                : translate(ONBOARDING_AGENT_NUDGE_GREETING_FALLBACK)}
                        </NudgeGreetingTitle>
                        <ClicksBlue size={26} />
                    </NudgeGreetingTitleRow>

                    <CloseButton
                        type="flat"
                        iconName="clear"
                        iconSize="xs"
                        onClick={onDismiss}
                        aria-label={translate(ONBOARDING_AGENT_NUDGE_DISMISS_ARIA)}
                        data-automation="onboarding-agent-nudge-dismiss"
                    />
                </NudgeHeader>
                <NudgeSuggestionList>
                    {suggestions.map((suggestionKey, suggestionIndex) => {
                        const question = translate(suggestionKey);
                        const handleClick = (): void => {
                            onboardingAgentTrackingService.trackNudgePromotionClick({
                                clickedText: question,
                                optionIndex: suggestionIndex + 1,
                                optionCount: suggestions.length,
                            });
                            onAsk(question);
                        };

                        return (
                            <NudgeSuggestionItem
                                key={suggestionKey}
                                type="button"
                                onClick={handleClick}
                                data-automation={`onboarding-agent-nudge-suggestion-${suggestionIndex}`}
                            >
                                <NudgeSuggestionText
                                    fontVariant="body1"
                                    color={colorsPalettes.carbon[400]}
                                >
                                    {question}
                                </NudgeSuggestionText>
                                <NudgeSuggestionIcon aria-hidden="true">
                                    <ChevronRight
                                        size={16}
                                        color="currentColor"
                                    />
                                </NudgeSuggestionIcon>
                            </NudgeSuggestionItem>
                        );
                    })}
                </NudgeSuggestionList>
                <NudgeInputRow>
                    <NudgeTextInput
                        type="text"
                        value={inputValue}
                        onChange={(event) => setInputValue(event.target.value)}
                        onKeyDown={handleInputKeyDown}
                        placeholder={translate(ONBOARDING_AGENT_NUDGE_INPUT_PLACEHOLDER)}
                        aria-label={translate(ONBOARDING_AGENT_NUDGE_INPUT_PLACEHOLDER)}
                        data-automation="onboarding-agent-nudge-input"
                    />

                    <NudgeInputSendButton
                        type="button"
                        $canSend={canSend}
                        disabled={!canSend}
                        onClick={handleInputSubmit}
                        aria-label={translate(ONBOARDING_AGENT_INPUT_SEND_ARIA)}
                        data-automation="onboarding-agent-nudge-send"
                    >
                        <ArrowUp
                            size="xs"
                            color={colorsPalettes.carbon[0]}
                        />
                    </NudgeInputSendButton>
                </NudgeInputRow>
            </NudgeCard>
        </NudgeSlot>
    );
};
